import { Decoration, EditorView } from "@codemirror/view";
import { StateField } from "@codemirror/state";
import { syntaxTree } from "@codemirror/language";

import {
    ancestorOfNodeWithType,
    isDirectChildOfEnvironment,
} from "components/organisms/manuscript/editor/latex/tree/ancestors";
import {
    getEnvironmentArguments as getTreeEnvironmentArguments,
    getEnvironmentName as getTreeEnvironmentName,
    parseFigureData,
} from "components/organisms/manuscript/editor/latex/tree/environments";
import { centeringNodeForEnvironment } from "components/organisms/manuscript/editor/latex/tree/figure";
import {
    isPrimaryMathNodeForContainer,
    mathAncestorNode,
    parseMathContainer,
} from "components/organisms/manuscript/editor/latex/tree/math";
import { parseTheoremArguments } from "components/organisms/manuscript/editor/latex/tree/theorems";
import { lineContainsOnlyNode } from "components/organisms/manuscript/editor/visual/utils/line";
import {
    extendBackwardsOverEmptyLines,
    extendForwardsOverEmptyLines,
} from "components/organisms/manuscript/editor/visual/interactions/selection";
import { generateTable, validateParsedTable } from "components/organisms/manuscript/editor/visual/table";
import {
    BeginWidget,
    MathWidget,
    BraceWidget,
    createCharacterCommand,
    createSpaceCommand,
    DescriptionItemWidget,
    DividerWidget,
    EditableGraphicsWidget,
    EditableInlineGraphicsWidget,
    EndWidget,
    EnvironmentLineWidget,
    FootnoteWidget,
    FrameWidget,
    GraphicsWidget,
    IndicatorWidget,
    IconBraceWidget,
    InlineGraphicsWidget,
    ItemWidget,
    LaTeXWidget,
    MakeTitleWidget,
    PreambleWidget,
    TabularWidget,
    TableRenderingErrorWidget,
    TeXWidget,
    TildeWidget,
    BeginTheoremWidget,
} from "../widgets/index.js";
import { preambleCollapsedState } from "../widgets/preamble.js";
import { selectionIntersects } from "components/organisms/manuscript/editor/visual/interactions/selection";
import { metadataState } from "components/organisms/manuscript/editor/latex/language";

function shouldDecorate(state, extents) {
    return state.readOnly || !selectionIntersects(state.selection, extents);
}

function shouldDecorateFromLineEdges(state, extents) {
    return shouldDecorate(state, {
        from: state.doc.lineAt(extents.from).from,
        to: state.doc.lineAt(extents.to).to,
    });
}

function getEnvironmentNameText(state, node) {
    return getTreeEnvironmentName(node, state) || "";
}

function isTabularLikeEnvironment(state, node) {
    const environmentName = getEnvironmentNameText(state, node);
    return ["tabular", "tabular*", "tabularx", "xltabular", "longtable"].includes(environmentName);
}

function extractArgumentText(state, node) {
    if (!node) {
        return "";
    }
    const open = node.getChild("OpenBrace") ?? node.getChild("OpenBracket");
    const close = node.getChild("CloseBrace") ?? node.getChild("CloseBracket");
    if (open && close && close.from >= open.to) {
        return state.sliceDoc(open.to, close.from).trim();
    }
    return state.sliceDoc(node.from, node.to).trim();
}

function getEnvironmentArguments(node) {
    return getTreeEnvironmentArguments(node);
}

function isExternalGraphicsPath(path) {
    return /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(path || "");
}

function resolvePreviewForPath(state, path, fallbackPreviewByPath = null) {
    if (!path) {
        return null;
    }

    const normalizedPath = String(path).replace(/^\/+/, "");
    const externalPath = isExternalGraphicsPath(path);
    const metadata = state.field(metadataState, false);
    const fileTreeData = Array.isArray(metadata?.fileTreeData) ? metadata.fileTreeData : [];
    const matchingNode = fileTreeData.find((node) => {
        if (node?.node_type !== "binary") {
            return false;
        }
        const candidatePath = String(node?.path_cache || node?.name || "").replace(/^\/+/, "");
        return candidatePath === normalizedPath;
    });

    const fallbackPreview = fallbackPreviewByPath?.(path);
    const extension =
        fallbackPreview?.extension || (normalizedPath.includes(".") ? normalizedPath.split(".").pop() : "");

    return {
        path,
        url:
            matchingNode?.file_url ||
            fallbackPreview?.url ||
            (externalPath ? path : path.startsWith("/") ? path : `/${path}`),
        extension,
    };
}

function decorateEnvironmentEdges(ranges, node, environmentName) {
    const beginNode = node.getChild("BeginEnv");
    const endNode = node.getChild("EndEnv");

    if (beginNode) {
        ranges.push(
            Decoration.replace({
                widget: new BeginWidget(environmentName),
                block: true,
            }).range(beginNode.from, beginNode.to),
        );
        ranges.push(
            Decoration.widget({
                widget: new EnvironmentLineWidget(environmentName, "begin"),
                block: true,
                side: -1,
            }).range(beginNode.from),
        );
    }

    if (endNode) {
        ranges.push(
            Decoration.widget({
                widget: new EnvironmentLineWidget(environmentName, "end"),
                block: true,
                side: 1,
            }).range(endNode.to),
        );
        ranges.push(
            Decoration.replace({
                widget: new EndWidget(),
                block: true,
            }).range(endNode.from, endNode.to),
        );
    }
}

function decorateArgumentBraces(
    startWidget,
    argumentNode,
    start,
    decorateEmptyArguments = false,
    endWidget,
    braceTypes = { open: "OpenBrace", close: "CloseBrace" },
) {
    if (!argumentNode) {
        return [];
    }

    const openBrace = argumentNode.getChild(braceTypes.open);
    const closeBrace = argumentNode.getChild(braceTypes.close);

    if (openBrace && closeBrace) {
        if (openBrace.to > start && (decorateEmptyArguments || argumentNode.to - argumentNode.from > 2)) {
            return [
                Decoration.replace({
                    widget: startWidget,
                }).range(start, openBrace.to),
                Decoration.replace({
                    widget: endWidget ?? new BraceWidget(),
                }).range(closeBrace.from, closeBrace.to),
            ];
        }
    }

    return [];
}

export function collectPreambleMeta(state, tree) {
    const preamble = {
        from: 0,
        to: 0,
        title: null,
        authors: [],
        date: null,
        maketitleFrom: null,
        maketitleTo: null,
    };

    let seenDocumentEnvironment = false;

    tree.iterate({
        enter(nodeRef) {
            if (nodeRef.node.type.is?.("Maketitle")) {
                const parentEnvironment = ancestorOfNodeWithType(nodeRef.node, "$Environment", "DocumentEnvironment");
                if (parentEnvironment?.type?.is?.("DocumentEnvironment")) {
                    preamble.to = nodeRef.node.from;
                }
                preamble.maketitleFrom = nodeRef.node.from;
                preamble.maketitleTo = nodeRef.node.to;
            }

            if (nodeRef.node.type.is?.("DocumentEnvironment")) {
                if (!seenDocumentEnvironment) {
                    preamble.to = nodeRef.node.getChild("Content")?.from ?? nodeRef.node.from;
                    seenDocumentEnvironment = true;
                }
            }

            if (nodeRef.type.name === "Title") {
                const argumentNode =
                    nodeRef.node.getChild("TextArgument") ?? nodeRef.node.getChild("ShortTextArgument");
                if (argumentNode) {
                    preamble.title = {
                        text: extractArgumentText(state, argumentNode),
                        editFrom: argumentNode.from + 1,
                        editTo: argumentNode.to - 1,
                    };
                    preamble.to = Math.max(preamble.to, nodeRef.node.to);
                }
            }

            if (nodeRef.type.name === "Author") {
                const argumentNode =
                    nodeRef.node.getChild("TextArgument") ?? nodeRef.node.getChild("ShortTextArgument");
                if (argumentNode) {
                    preamble.authors.push({
                        text: extractArgumentText(state, argumentNode),
                        editFrom: argumentNode.from + 1,
                        editTo: argumentNode.to - 1,
                    });
                    preamble.to = Math.max(preamble.to, nodeRef.node.to);
                }
            }

            if (nodeRef.type.name === "Date") {
                const argumentNode =
                    nodeRef.node.getChild("TextArgument") ?? nodeRef.node.getChild("ShortTextArgument");
                if (argumentNode) {
                    preamble.date = {
                        text: extractArgumentText(state, argumentNode),
                        editFrom: argumentNode.from + 1,
                        editTo: argumentNode.to - 1,
                    };
                    preamble.to = Math.max(preamble.to, nodeRef.node.to);
                }
            }
        },
    });

    return preamble;
}

function buildDecorations(state, options = {}) {
    const { previewByPath = null } = options;
    const resolvePreview = (path) => resolvePreviewForPath(state, path, previewByPath);
    const preambleCollapsed = state.field(preambleCollapsedState, false) ?? true;
    const tree = syntaxTree(state);
    const preambleMeta = collectPreambleMeta(state, tree);
    const ranges = [];

    if (preambleMeta.to > preambleMeta.from) {
        if (preambleCollapsed) {
            ranges.push(
                Decoration.replace({
                    widget: new PreambleWidget(false),
                    block: true,
                }).range(preambleMeta.from, preambleMeta.to),
            );
        } else {
            ranges.push(
                Decoration.widget({
                    widget: new PreambleWidget(true),
                    block: true,
                    side: -1,
                }).range(preambleMeta.from),
            );
        }
    }

    if (preambleMeta.maketitleFrom != null && preambleMeta.maketitleTo != null) {
        const line = state.doc.lineAt(preambleMeta.maketitleFrom);
        const from = line.from;
        const to = state.doc.lineAt(preambleMeta.maketitleTo).to;
        ranges.push(
            Decoration.replace({
                widget: new MakeTitleWidget({
                    kind: "maketitle",
                    title: preambleMeta.title,
                    author:
                        preambleMeta.authors.length > 0
                            ? {
                                  text: preambleMeta.authors.map((entry) => entry.text).join(" \\and "),
                                  editFrom: preambleMeta.authors[0].editFrom,
                                  editTo: preambleMeta.authors[preambleMeta.authors.length - 1].editTo,
                              }
                            : null,
                    date: preambleMeta.date,
                }),
                block: true,
            }).range(from, to),
        );
    }
    const listEnvironmentStack = [];
    const ordinalStack = [];
    let currentListEnvironment;
    let currentOrdinal = 0;
    let listDepth = 0;
    const theoremEnvironments = new Map([
        ["theorem", "Theorem"],
        ["corollary", "Corollary"],
        ["lemma", "Lemma"],
        ["proof", "Proof"],
    ]);

    tree.iterate({
        enter(nodeRef) {
            if (nodeRef.type.name === "KnownEnvironment" || nodeRef.type.name === "Environment") {
                const firstChild = nodeRef.node.firstChild;
                const environmentNode = firstChild?.type?.name?.endsWith("Environment") ? firstChild : nodeRef.node;
                const environmentName = getEnvironmentNameText(state, environmentNode);

                if (["itemize", "enumerate", "description"].includes(environmentName)) {
                    if (currentListEnvironment) {
                        listEnvironmentStack.push(currentListEnvironment);
                        ordinalStack.push(currentOrdinal);
                    }
                    currentListEnvironment = environmentName;
                    currentOrdinal = 0;
                    listDepth += 1;
                }

                if (["abstract"].includes(environmentName)) {
                    decorateEnvironmentEdges(ranges, environmentNode, environmentName);
                }

                if (
                    [
                        "figure",
                        "table",
                        "quote",
                        "quotation",
                        "quoting",
                        "displayquote",
                        "verbatim",
                        "lstlisting",
                    ].includes(environmentName)
                ) {
                    const beginNode = environmentNode.getChild("BeginEnv");
                    const endNode = environmentNode.getChild("EndEnv");

                    if (beginNode && endNode) {
                        const beginLine = state.doc.lineAt(beginNode.from);
                        const endLine = state.doc.lineAt(endNode.from);
                        const begin = {
                            from: beginLine.from,
                            to: extendForwardsOverEmptyLines(state.doc, beginLine),
                        };
                        const end = {
                            from: extendBackwardsOverEmptyLines(state.doc, endLine),
                            to: endLine.to,
                        };

                        if (shouldDecorateFromLineEdges(state, { from: begin.from, to: end.to })) {
                            ranges.push(
                                Decoration.replace({
                                    widget: new EnvironmentLineWidget(environmentName, "begin"),
                                    block: true,
                                }).range(begin.from, begin.to),
                            );
                            ranges.push(
                                Decoration.replace({
                                    widget: new EnvironmentLineWidget(environmentName, "end"),
                                    block: true,
                                }).range(end.from, end.to),
                            );

                            const centeringNode =
                                environmentName === "figure" || environmentName === "table"
                                    ? centeringNodeForEnvironment({ node: environmentNode })
                                    : null;

                            if (centeringNode) {
                                const line = state.doc.lineAt(centeringNode.from);
                                ranges.push(
                                    Decoration.replace({
                                        block: true,
                                    }).range(
                                        extendBackwardsOverEmptyLines(state.doc, line),
                                        extendForwardsOverEmptyLines(state.doc, line),
                                    ),
                                );
                            }
                        }
                    }
                }

                if (["itemize", "enumerate", "description"].includes(environmentName)) {
                    const beginNode = environmentNode.getChild("BeginEnv");
                    const endNode = environmentNode.getChild("EndEnv");

                    if (beginNode && endNode) {
                        const beginLine = state.doc.lineAt(beginNode.from);
                        const endLine = state.doc.lineAt(endNode.from);

                        if (
                            !selectionIntersects(state.selection, { from: beginLine.from, to: beginLine.to }) &&
                            lineContainsOnlyNode(beginLine, beginNode)
                        ) {
                            ranges.push(
                                Decoration.replace({
                                    block: true,
                                }).range(beginLine.from, beginLine.to),
                            );
                        }

                        if (
                            !selectionIntersects(state.selection, { from: endLine.from, to: endLine.to }) &&
                            lineContainsOnlyNode(endLine, endNode)
                        ) {
                            ranges.push(
                                Decoration.replace({
                                    block: true,
                                }).range(endLine.from, endLine.to),
                            );
                        }
                    }
                }
            }

            if (nodeRef.type.name === "BeginEnv") {
                const environmentName = getEnvironmentNameText(state, nodeRef.node);

                if (environmentName === "frame" && shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                    const parent = nodeRef.node.parent;
                    const args = getEnvironmentArguments(parent);
                    if (args.length > 0) {
                        const frame = {
                            title: {
                                node: args[0],
                                content: extractArgumentText(state, args[0]),
                            },
                        };
                        if (args[1]) {
                            frame.subtitle = {
                                node: args[1],
                                content: extractArgumentText(state, args[1]),
                            };
                        }
                        ranges.push(
                            Decoration.replace({
                                widget: new FrameWidget(frame),
                                block: true,
                            }).range(nodeRef.from, args[1]?.to ?? args[0].to),
                        );
                    }
                }

                const theoremName = theoremEnvironments.get(environmentName);
                if (theoremName && shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                    const optionalArgument = nodeRef.node.getChild("OptionalArgument");
                    ranges.push(
                        Decoration.replace({
                            widget: new BeginTheoremWidget(
                                environmentName,
                                theoremName,
                                extractArgumentText(state, optionalArgument),
                            ),
                            block: true,
                        }).range(nodeRef.from, nodeRef.to),
                    );
                }
            }

            if (nodeRef.type.name === "NewTheoremCommand") {
                const result = parseTheoremArguments(state, nodeRef.node);
                if (result) {
                    const { name, label } = result;
                    theoremEnvironments.set(name, label);
                }
            }

            if (nodeRef.type.name === "EndEnv") {
                const environmentName = getEnvironmentNameText(state, nodeRef.node);

                if (environmentName === "frame" && shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                    ranges.push(
                        Decoration.replace({
                            widget: new DividerWidget(),
                            block: true,
                        }).range(nodeRef.from, nodeRef.to),
                    );
                }

                if (
                    theoremEnvironments.has(environmentName) &&
                    shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })
                ) {
                    ranges.push(
                        Decoration.replace({
                            widget: new EndWidget(),
                            block: true,
                        }).range(nodeRef.from, nodeRef.to),
                    );
                }
            }

            if (nodeRef.type.name === "$SectioningCtrlSeq") {
                const ancestorNode = ancestorOfNodeWithType(nodeRef.node, "SectioningCommand");
                if (ancestorNode) {
                    const argumentNode = ancestorNode.getChild("SectioningArgument");
                    if (argumentNode) {
                        const openBrace = argumentNode.getChild("OpenBrace");
                        const closeBrace = argumentNode.getChild("CloseBrace");
                        const sectionCtrlSeqNode = ancestorNode.getChild("$CtrlSeq");
                        const titleNode = argumentNode.getChild("LongArg");

                        if (!openBrace || !closeBrace || !sectionCtrlSeqNode || !titleNode) {
                            return;
                        }

                        const title = state.sliceDoc(titleNode.from, titleNode.to);
                        if (!title.trim()) {
                            return;
                        }

                        const showBraces =
                            selectionIntersects(state.selection, {
                                from: sectionCtrlSeqNode.from,
                                to: sectionCtrlSeqNode.to,
                            }) ||
                            selectionIntersects(state.selection, { from: openBrace.from, to: openBrace.to }) ||
                            selectionIntersects(state.selection, { from: closeBrace.from, to: closeBrace.to });

                        ranges.push(
                            Decoration.replace({
                                widget: new BraceWidget(showBraces ? "{" : ""),
                            }).range(nodeRef.from, titleNode.from),
                        );

                        ranges.push(
                            Decoration.replace({
                                widget: new BraceWidget(showBraces ? "}" : ""),
                            }).range(closeBrace.from, closeBrace.to),
                        );

                        return;
                    }
                }
            }

            if (nodeRef.node.type.is?.("IncludeGraphics") || nodeRef.node.type.is?.("IncludeSvg")) {
                const semanticNode = nodeRef.node;
                const isIncludeSvg = semanticNode.type.is?.("IncludeSvg");
                const argumentNodeName = isIncludeSvg ? "IncludeSvgArgument" : "IncludeGraphicsArgument";
                const argumentNode = semanticNode.getChild(argumentNodeName);
                const optionalNode = argumentNode?.getChild("OptionalArgument");
                const pathNode =
                    argumentNode?.getChild("FilePathArgument")?.getChild("LiteralArgContent") ??
                    argumentNode?.getChild("BareFilePathArgument") ??
                    semanticNode.getChild("TextArgument") ??
                    semanticNode.getChild("FilePathArgument") ??
                    semanticNode.getChild("BareFilePathArgument");

                if (optionalNode) {
                    ranges.push(
                        ...decorateArgumentBraces(
                            new BraceWidget("["),
                            optionalNode,
                            semanticNode.from,
                            true,
                            new BraceWidget("]"),
                            { open: "OpenBracket", close: "CloseBracket" },
                        ),
                    );
                }

                if (pathNode) {
                    const pathStart = optionalNode ? optionalNode.to : semanticNode.from;
                    ranges.push(
                        ...decorateArgumentBraces(new BraceWidget(), pathNode, pathStart, false, new BraceWidget()),
                    );

                    if (shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                        const pathText = state.sliceDoc(pathNode.from, pathNode.to).trim();
                        if (!isIncludeSvg && pathText.toLowerCase().endsWith(".svg")) {
                            return;
                        }
                        const figureAncestor = ancestorOfNodeWithType(nodeRef.node, "FigureEnvironment");
                        const figureData = figureAncestor ? parseFigureData(figureAncestor, state) : null;
                        const centered = Boolean(
                            figureAncestor && centeringNodeForEnvironment({ node: figureAncestor }),
                        );
                        const line = state.doc.lineAt(nodeRef.from);
                        const Widget = figureAncestor
                            ? state.readOnly
                                ? GraphicsWidget
                                : EditableGraphicsWidget
                            : state.readOnly
                              ? InlineGraphicsWidget
                              : EditableInlineGraphicsWidget;

                        if (lineContainsOnlyNode(line, nodeRef)) {
                            ranges.push(
                                Decoration.replace({
                                    widget: new Widget(
                                        figureData?.file?.path || pathText,
                                        resolvePreview(figureData?.file?.path || pathText),
                                        centered,
                                        figureData,
                                    ),
                                    block: true,
                                }).range(line.from, line.to),
                            );
                        } else {
                            ranges.push(
                                Decoration.replace({
                                    widget: new Widget(
                                        figureData?.file?.path || pathText,
                                        resolvePreview(figureData?.file?.path || pathText),
                                        centered,
                                        figureData,
                                    ),
                                }).range(nodeRef.from, nodeRef.to),
                            );
                        }
                    }
                }
                return;
            }

            if (nodeRef.type.name === "Command") {
                const raw = state.sliceDoc(nodeRef.from, nodeRef.to);

                const semanticNode = nodeRef.node.firstChild;
                if (
                    semanticNode?.type?.name === "Title" ||
                    semanticNode?.type?.name === "Author" ||
                    semanticNode?.type?.name === "Date"
                ) {
                    const argumentNode =
                        semanticNode.getChild("TextArgument") ?? semanticNode.getChild("ShortTextArgument");
                    ranges.push(
                        ...decorateArgumentBraces(
                            new BraceWidget(),
                            argumentNode,
                            nodeRef.from,
                            false,
                            new BraceWidget(),
                        ),
                    );
                }

                if (semanticNode?.type?.name === "Caption") {
                    const argumentNode =
                        semanticNode.getChild("TextArgument") ?? semanticNode.getChild("ShortTextArgument");
                    ranges.push(
                        ...decorateArgumentBraces(
                            new BraceWidget(),
                            argumentNode,
                            nodeRef.from,
                            false,
                            new BraceWidget(),
                        ),
                    );
                }

                if (semanticNode?.type?.name === "FootnoteCommand") {
                    ranges.push(
                        Decoration.replace({
                            widget: new FootnoteWidget("footnote"),
                        }).range(nodeRef.from, nodeRef.to),
                    );
                }

                if (semanticNode?.type?.name === "EndnoteCommand") {
                    ranges.push(
                        Decoration.replace({
                            widget: new FootnoteWidget("endnote"),
                        }).range(nodeRef.from, nodeRef.to),
                    );
                }

                if (semanticNode?.type?.name === "Cite") {
                    const argumentNode = semanticNode.getChild("BibKeyArgument")?.getChild("ShortTextArgument");
                    if (argumentNode && shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                        ranges.push(
                            ...decorateArgumentBraces(
                                new IconBraceWidget("📚"),
                                argumentNode,
                                nodeRef.from,
                                true,
                                new BraceWidget(),
                            ),
                        );
                    }
                    return;
                }

                if (semanticNode?.type?.name === "Ref") {
                    const argumentNode = semanticNode.getChild("RefArgument")?.getChild("ShortTextArgument");
                    const contentNode = argumentNode?.getChild("ShortArg");
                    const shouldShowBraces =
                        !shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to }) ||
                        contentNode?.from === contentNode?.to;

                    if (argumentNode) {
                        ranges.push(
                            ...decorateArgumentBraces(
                                new IconBraceWidget(shouldShowBraces ? "🏷{" : "🏷"),
                                argumentNode,
                                nodeRef.from,
                                true,
                                new BraceWidget(shouldShowBraces ? "}" : ""),
                            ),
                        );
                    }
                    return;
                }

                if (semanticNode?.type?.name === "Label") {
                    const argumentNode = semanticNode.getChild("LabelArgument")?.getChild("ShortTextArgument");
                    if (argumentNode && shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                        ranges.push(
                            ...decorateArgumentBraces(
                                new IconBraceWidget("🏷"),
                                argumentNode,
                                nodeRef.from,
                                true,
                                new BraceWidget(),
                            ),
                        );
                    }
                    return;
                }

                if (semanticNode?.type?.name === "Include") {
                    const argumentNode = semanticNode.getChild("IncludeArgument")?.getChild("FilePathArgument");
                    if (argumentNode && shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                        ranges.push(
                            ...decorateArgumentBraces(
                                new IconBraceWidget("🔗"),
                                argumentNode,
                                nodeRef.from,
                                true,
                                new BraceWidget(),
                            ),
                        );
                    }
                    return;
                }

                if (semanticNode?.type?.name === "Input") {
                    const argumentNode = semanticNode.getChild("InputArgument");
                    if (argumentNode && argumentNode.to - argumentNode.from > 2) {
                        ranges.push(
                            Decoration.replace({
                                widget: new IconBraceWidget("🔗"),
                            }).range(nodeRef.from, argumentNode.from + 1),
                        );
                        ranges.push(
                            Decoration.replace({
                                widget: new BraceWidget(),
                            }).range(nodeRef.to - 1, nodeRef.to),
                        );
                    }
                    return;
                }

                if (semanticNode?.type?.name === "Subfile") {
                    const argumentNode = semanticNode.getChild("SubfileArgument");
                    if (argumentNode && argumentNode.to - argumentNode.from > 2) {
                        ranges.push(
                            Decoration.replace({
                                widget: new IconBraceWidget("🔗"),
                            }).range(nodeRef.from, argumentNode.from + 1),
                        );
                        ranges.push(
                            Decoration.replace({
                                widget: new BraceWidget(),
                            }).range(nodeRef.to - 1, nodeRef.to),
                        );
                    }
                    return;
                }

                if (semanticNode?.type?.name === "UrlCommand") {
                    const argumentNode = semanticNode.getChild("UrlArgument");
                    const contentNode = argumentNode?.getChild("LiteralArgContent");
                    const shouldShowBraces =
                        !shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to }) ||
                        contentNode?.from === contentNode?.to;
                    if (argumentNode) {
                        ranges.push(
                            ...decorateArgumentBraces(
                                new BraceWidget(shouldShowBraces ? "{" : ""),
                                argumentNode,
                                nodeRef.from,
                                false,
                                new BraceWidget(shouldShowBraces ? "}" : ""),
                            ),
                        );
                    }
                    return;
                }

                if (semanticNode?.type?.name === "HrefCommand") {
                    const contentArgumentNode = semanticNode.getChild("ShortTextArgument");
                    const contentNode = contentArgumentNode?.getChild("ShortArg");
                    const shouldShowBraces =
                        !shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to }) ||
                        contentNode?.from === contentNode?.to;
                    if (contentArgumentNode) {
                        ranges.push(
                            ...decorateArgumentBraces(
                                new BraceWidget(shouldShowBraces ? "{" : ""),
                                contentArgumentNode,
                                nodeRef.from,
                                true,
                                new BraceWidget(shouldShowBraces ? "}" : ""),
                            ),
                        );
                    }
                    return;
                }

                if (
                    [
                        "TextBoldCommand",
                        "TextItalicCommand",
                        "TextSmallCapsCommand",
                        "TextTeletypeCommand",
                        "TextMediumCommand",
                        "TextSansSerifCommand",
                        "TextSuperscriptCommand",
                        "TextSubscriptCommand",
                        "StrikeOutCommand",
                        "EmphasisCommand",
                        "UnderlineCommand",
                    ].includes(semanticNode?.type?.name)
                ) {
                    const textArgumentNode = semanticNode.getChild("TextArgument");
                    const argumentText = textArgumentNode?.getChild("LongArg");
                    const shouldShowBraces =
                        !shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to }) ||
                        argumentText?.from === argumentText?.to;
                    if (textArgumentNode) {
                        ranges.push(
                            ...decorateArgumentBraces(
                                new BraceWidget(shouldShowBraces ? "{" : ""),
                                textArgumentNode,
                                nodeRef.from,
                                true,
                                new BraceWidget(shouldShowBraces ? "}" : ""),
                            ),
                        );
                    }
                    return;
                }

                if (["MathTextCommand", "ParBoxCommand"].includes(semanticNode?.type?.name)) {
                    const textArgumentNode = semanticNode.getChild("TextArgument");
                    if (textArgumentNode && shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                        ranges.push(
                            ...decorateArgumentBraces(
                                new BraceWidget(),
                                textArgumentNode,
                                nodeRef.from,
                                true,
                                new BraceWidget(),
                            ),
                        );
                    }
                    return;
                }

                if (raw.startsWith("\\LaTeX")) {
                    ranges.push(
                        Decoration.replace({
                            widget: new LaTeXWidget(),
                        }).range(nodeRef.from, nodeRef.to),
                    );
                    return;
                }

                if (raw.startsWith("\\TeX")) {
                    ranges.push(
                        Decoration.replace({
                            widget: new TeXWidget(),
                        }).range(nodeRef.from, nodeRef.to),
                    );
                    return;
                }

                if (raw === "\\~") {
                    ranges.push(
                        Decoration.replace({
                            widget: new TildeWidget(),
                        }).range(nodeRef.from, nodeRef.to),
                    );
                    return;
                }

                const characterWidget = createCharacterCommand(raw);
                if (characterWidget) {
                    ranges.push(
                        Decoration.replace({
                            widget: characterWidget,
                        }).range(nodeRef.from, nodeRef.to),
                    );
                    return;
                }

                const spaceWidget = createSpaceCommand(raw);
                if (spaceWidget) {
                    ranges.push(
                        Decoration.replace({
                            widget: spaceWidget,
                        }).range(nodeRef.from, nodeRef.to),
                    );
                    return;
                }
            }

            if (nodeRef.type.is?.("Cite")) {
                if (shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                    const argumentNode = nodeRef.node.getChild("BibKeyArgument")?.getChild("ShortTextArgument");
                    ranges.push(
                        ...decorateArgumentBraces(
                            new IconBraceWidget("📚"),
                            argumentNode,
                            nodeRef.from,
                            false,
                            new BraceWidget(),
                        ),
                    );
                }
                return;
            }

            if (nodeRef.type.is?.("Ref")) {
                const argumentNode = nodeRef.node.getChild("RefArgument")?.getChild("ShortTextArgument");
                const shouldShowBraces =
                    !shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to }) ||
                    argumentNode?.from === argumentNode?.to;

                ranges.push(
                    ...decorateArgumentBraces(
                        new IconBraceWidget(shouldShowBraces ? "🏷{" : "🏷"),
                        argumentNode,
                        nodeRef.from,
                        true,
                        new BraceWidget(shouldShowBraces ? "}" : ""),
                    ),
                );
                return;
            }

            if (nodeRef.type.is?.("Label")) {
                if (shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                    const argumentNode = nodeRef.node.getChild("LabelArgument")?.getChild("ShortTextArgument");
                    ranges.push(
                        ...decorateArgumentBraces(
                            new IconBraceWidget("🏷"),
                            argumentNode,
                            nodeRef.from,
                            false,
                            new BraceWidget(),
                        ),
                    );
                }
                return;
            }

            if (nodeRef.type.is?.("Caption")) {
                if (shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                    const argumentNode =
                        nodeRef.node.getChild("TextArgument") ?? nodeRef.node.getChild("ShortTextArgument");
                    ranges.push(
                        ...decorateArgumentBraces(
                            new BraceWidget(),
                            argumentNode,
                            nodeRef.from,
                            false,
                            new BraceWidget(),
                        ),
                    );
                }
                return;
            }

            if (nodeRef.type.is?.("FootnoteCommand") || nodeRef.type.is?.("EndnoteCommand")) {
                const textArgumentNode = nodeRef.node.getChild("TextArgument");
                if (textArgumentNode) {
                    if (
                        state.readOnly &&
                        selectionIntersects(state.selection, { from: nodeRef.from, to: nodeRef.to })
                    ) {
                        ranges.push(
                            ...decorateArgumentBraces(
                                new BraceWidget(),
                                textArgumentNode,
                                nodeRef.from,
                                false,
                                new BraceWidget(),
                            ),
                            Decoration.mark({
                                class: "ol-cm-footnote ol-cm-footnote-view",
                            }).range(textArgumentNode.from, textArgumentNode.to),
                        );
                    } else if (shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                        ranges.push(
                            Decoration.replace({
                                widget: new FootnoteWidget(nodeRef.type.is("FootnoteCommand") ? "footnote" : "endnote"),
                            }).range(nodeRef.from, nodeRef.to),
                        );
                        return;
                    }
                }
            }

            if (nodeRef.type.name === "VerbCommand") {
                if (shouldDecorate(state, { from: nodeRef.from, to: nodeRef.to })) {
                    const contentNode = nodeRef.node.getChild("VerbContent");
                    if (contentNode && contentNode.to - contentNode.from > 2) {
                        ranges.push(Decoration.replace({}).range(nodeRef.from, contentNode.from + 1));

                        ranges.push(Decoration.replace({}).range(nodeRef.to - 1, nodeRef.to));
                    }
                }
                return;
            }

            if (nodeRef.type.name === "LineBreak") {
                ranges.push(
                    Decoration.replace({
                        widget: new IndicatorWidget("\u21A9"),
                    }).range(nodeRef.from, nodeRef.to),
                );
            }

            if (nodeRef.type.name === "Item") {
                if (currentListEnvironment) {
                    currentOrdinal += 1;
                    const line = state.doc.lineAt(nodeRef.from);
                    const onlySpaceBeforeNode = /^\s*$/.test(state.sliceDoc(line.from, nodeRef.from));
                    const from = onlySpaceBeforeNode ? line.from : nodeRef.from;

                    if (currentListEnvironment === "description") {
                        const argumentNode = nodeRef.node.getChild("OptionalArgument");
                        const to = argumentNode ? argumentNode.from : nodeRef.to;
                        const onlySpaceAfterNode = !argumentNode && /^\s*$/.test(state.sliceDoc(nodeRef.to, line.to));

                        if (!onlySpaceAfterNode) {
                            ranges.push(
                                Decoration.replace({
                                    widget: new DescriptionItemWidget(listDepth),
                                }).range(from, to),
                            );
                        }

                        if (argumentNode) {
                            const decorateBrackets = shouldDecorate(state, {
                                from: argumentNode.from,
                                to: argumentNode.to,
                            });

                            ranges.push(
                                ...decorateArgumentBraces(
                                    new BraceWidget(decorateBrackets ? "" : "["),
                                    argumentNode,
                                    argumentNode.from,
                                    false,
                                    new BraceWidget(decorateBrackets ? "" : "]"),
                                    { open: "OpenBracket", close: "CloseBracket" },
                                ),
                            );
                        }
                    } else {
                        ranges.push(
                            Decoration.replace({
                                widget: new ItemWidget(currentListEnvironment || "document", currentOrdinal, listDepth),
                            }).range(from, nodeRef.to),
                        );
                    }
                }
                return;
            }

            if (nodeRef.type.name === "Math") {
                const ancestorNode = mathAncestorNode(state, nodeRef.from);
                if (
                    ancestorNode &&
                    isPrimaryMathNodeForContainer(nodeRef, ancestorNode) &&
                    shouldDecorate(state, { from: ancestorNode.from, to: ancestorNode.to })
                ) {
                    const math = parseMathContainer(state, nodeRef, ancestorNode);
                    if (math && math.passToMathJax) {
                        ranges.push(
                            Decoration.replace({
                                widget: new MathWidget(math.content, math.displayMode, ""),
                                block: math.displayMode,
                            }).range(ancestorNode.from, ancestorNode.to),
                        );
                    }
                }
                return;
            }

            if (nodeRef.type.name === "SectioningCommand") {
                const argumentNode = nodeRef.node.getChild("SectioningArgument");
                const openBrace = argumentNode?.getChild("OpenBrace");
                const closeBrace = argumentNode?.getChild("CloseBrace");
                const controlNode = nodeRef.node.getChild("$CtrlSeq");
                const showBraces =
                    (openBrace && selectionIntersects(state.selection, openBrace)) ||
                    (closeBrace && selectionIntersects(state.selection, closeBrace)) ||
                    (controlNode && selectionIntersects(state.selection, controlNode));
                ranges.push(
                    ...decorateArgumentBraces(
                        new BraceWidget(showBraces ? "{" : ""),
                        argumentNode,
                        nodeRef.from,
                        false,
                        new BraceWidget(showBraces ? "}" : ""),
                    ),
                );
            }

            if (nodeRef.type.name === "KnownEnvironment") {
                const environmentNode = nodeRef.node.firstChild;

                if (environmentNode && isTabularLikeEnvironment(state, environmentNode)) {
                    const beginNode = environmentNode.getChild("BeginEnv");
                    const envNameGroup = beginNode?.getChild("EnvNameGroup");
                    const textArgument = beginNode?.getChild("TextArgument");
                    const tableNode = ancestorOfNodeWithType(environmentNode, "TableEnvironment");
                    const directTableChild = tableNode
                        ? isDirectChildOfEnvironment(environmentNode.parent, tableNode)
                        : false;

                    if (envNameGroup) {
                        ranges.push(
                            ...decorateArgumentBraces(
                                new BraceWidget(),
                                envNameGroup,
                                beginNode.from + "\\begin".length,
                                true,
                                new BraceWidget(),
                            ),
                        );
                    }

                    if (textArgument) {
                        ranges.push(
                            ...decorateArgumentBraces(
                                new BraceWidget(),
                                textArgument,
                                envNameGroup ? envNameGroup.to : beginNode.from,
                                true,
                                new BraceWidget(),
                            ),
                        );
                    }

                    if (shouldDecorate(state, environmentNode)) {
                        try {
                            const parsedTableData = generateTable(environmentNode, state);
                            const validTable = validateParsedTable(parsedTableData);
                            if (!validTable) {
                                throw new Error("Invalid parsed table");
                            }
                            ranges.push(
                                Decoration.replace({
                                    widget: new TabularWidget(
                                        parsedTableData,
                                        environmentNode,
                                        state.doc.sliceString(environmentNode.from, environmentNode.to),
                                        tableNode,
                                        directTableChild,
                                    ),
                                    block: true,
                                }).range(nodeRef.from, nodeRef.to),
                            );
                            return false;
                        } catch (_error) {
                            ranges.push(
                                Decoration.widget({
                                    widget: new TableRenderingErrorWidget(tableNode),
                                    block: true,
                                }).range(nodeRef.from),
                            );
                        }
                    }
                }
            }
        },
        leave(nodeRef) {
            if (nodeRef.type.name === "KnownEnvironment" || nodeRef.type.name === "Environment") {
                const firstChild = nodeRef.node.firstChild;
                const environmentNode = firstChild?.type?.name?.endsWith("Environment") ? firstChild : nodeRef.node;
                const environmentName = getEnvironmentNameText(state, environmentNode);

                if (["itemize", "enumerate", "description"].includes(environmentName)) {
                    currentListEnvironment = listEnvironmentStack.pop();
                    currentOrdinal = ordinalStack.pop() ?? 0;
                    listDepth = Math.max(0, listDepth - 1);
                }
            }
        },
    });

    ranges.sort(
        (left, right) =>
            left.from - right.from ||
            (left.value?.startSide || 0) - (right.value?.startSide || 0) ||
            left.to - right.to,
    );

    const decorations = Decoration.set(ranges, true);
    return {
        decorations,
        preamble: preambleMeta,
    };
}

export const atomicDecorations = (options = {}) =>
    StateField.define({
        create(state) {
            return buildDecorations(state, options);
        },
        update(_value, transaction) {
            if (transaction.docChanged || transaction.selection || transaction.viewportChanged) {
                return buildDecorations(transaction.state, options);
            }

            return buildDecorations(transaction.state, options);
        },
        provide(field) {
            return [
                EditorView.decorations.from(field, (value) => value.decorations),
                EditorView.atomicRanges.from(field, (value) => () => value.decorations),
            ];
        },
    });
